import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * BackButton — a small, universal "← Back" affordance at the top of every page
 * so users never have to use the browser's back button (and lose form state)
 * or refresh.
 *
 * Behaviour:
 *  - If there is in-app history (i.e. the user navigated here from another
 *    Vault page), pop one entry (window.history.back-style).
 *  - Otherwise, fall back to `/` so the button is never a dead-end.
 *  - Hidden on the homepage itself.
 *
 * Props:
 *  - fallback (string, default "/")
 *  - label (string, default "Back")
 *  - className (string, optional extra classes for layout
 */
export default function BackButton({ fallback = "/", label = "Back", className = "" }) {
  const nav = useNavigate();
  const loc = useLocation();
  // Skip rendering on the homepage — nothing to go "back" to.
  if (loc.pathname === "/") return null;

  const onClick = () => {
    // History length includes the initial entry, so >1 means we have somewhere
    // to pop back to within this tab session. Cross-origin entries still count
    // here, but nav(-1) on cross-origin will fall through harmlessly.
    if (window.history.length > 1 && document.referrer && document.referrer.startsWith(window.location.origin)) {
      nav(-1);
    } else if (window.history.length > 2) {
      nav(-1);
    } else {
      nav(fallback);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="back-button"
      aria-label={label}
      className={`inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 hover:text-white transition-colors ${className}`}
    >
      <ArrowLeft size={11} /> {label}
    </button>
  );
}
